import React, { useContext, useEffect } from 'react';
import { Context as AuthContext } from 'contexts/auth';
import { Context as CompanyContext } from 'contexts/company';
import { useAggregate } from 'hooks';
import { Config } from 'configs';
import { useNavigate } from 'react-router-dom';
import { ListLayout } from 'components/layouts/listLayout';
import { PageTitle } from 'components/layouts/pageTitle';
import { PageContent } from 'components/layouts/pageContent';
import { DataColumn, LinkColumn, List, ListHeader } from 'components/lists';
import { NoRestaurantsBlock } from 'components/blocks/noRestaurants';

export const Menus = () => {
  const navigate = useNavigate();

  const { state: auth } = useContext(AuthContext);
  const { state: companyState, deleteById, shouldDisplayNoRestaurantBlock, getCompanyRestaurants } = useContext(CompanyContext);

  const requestData = {
    collectionName: Config.DATA.COLLECTIONS.RESTAURANTS,
    aggregateId: Config.DATA.AGGREGATES.GET_MENUS,
    aggregateIdV2: 'AdminListMenus',
    secretKey: auth.token,
    filterBuilder: () => {
      return { companyId: auth.company?.company };
    },
    loadOnInit: true,
    companyIdForV2: auth.company?.company,
  };

  const onEditClicked = (item) => {
    navigate(`/menu/${item._id}`);
  };

  const [findMenus, menusResponse, isLoading] = useAggregate(requestData);

  useEffect(() => {
    getCompanyRestaurants({ token: auth.token, companyId: auth.company?.company });
  }, []);

  const onDeleteClicked = async (item) => {
    await deleteById(
      {
        collectionName: Config.DATA.COLLECTIONS.MENU,
        token: auth.token,
        id: item._id,
        resourceV2: 'AdminMenu',
        companyIdForV2: auth.company?.company,
      },
      async () => {
        await findMenus(true);
      },
    );
  };

  if (isLoading !== Config.DATA.LOADING_STATUS.INITIAL_LOAD && shouldDisplayNoRestaurantBlock()) {
    return <NoRestaurantsBlock />;
  }

  return (
    <ListLayout isLoading={isLoading === Config.DATA.LOADING_STATUS.INITIAL_LOAD || companyState.isLoadingRestaurants}>
      <PageTitle title="Menu" isTopLevel />

      <PageContent>
        <ListHeader
          title="Overview"
          description={`Menus of ${auth.company?.display_name}`}
        >
          <button
            type="button"
            onClick={() => navigate('/menu/add-new')}
            className="inline-flex items-center justify-center rounded-md border border-transparent bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 sm:w-auto"
          >
            Add new
          </button>
        </ListHeader>

        <List source={menusResponse} onRowClick={onEditClicked}>
          <DataColumn
            name="Restaurants"
            dbColumnName="restaurantNames"
            render={(menu) => {
              if (!menu.restaurantNames || menu.restaurantNames.length === 0) {
                return '';
              }

              return menu.restaurantNames.join(', ');
            }}
            headerClass="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
            dataCellClass="px-6 py-4 whitespace-nowrap text-sm text-gray-500"
          />
          <DataColumn
            name="Menu type"
            dbColumnName="type"
            render={(menu) => {
              return menu.type;
            }}
            headerClass="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
            dataCellClass="px-6 py-4 whitespace-nowrap text-sm text-gray-500"
          />
          <DataColumn
            name="Categories"
            dbColumnName="categories"
            render={(menu) => {
              return menu.categories ? menu.categories.length : 0;
            }}
            headerClass="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
            dataCellClass="px-6 py-4 whitespace-nowrap text-sm text-gray-500"
          />
          <LinkColumn
            title="Delete"
            onClick={onDeleteClicked}
            askForConfirmation
            className="relative py-3.5 pl-3 sm:pr-6"
            dataCellClass="whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-6"
          />
        </List>
      </PageContent>
    </ListLayout>
  );
};
